import type { PlanVo } from "@/api";
import creditIcon from "@/assets/images/credit-icon.png";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { formatNumberWithCommas } from "@/util/formatNumber";
import { Popover } from "antd";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { DISCORD_INVITE_URL } from "../../../common/const";
import { isFreePlan } from "../Pricing.helpers";
import CheckedIcon from "../assets/checked-icon.svg";
import { PlanButton } from "./PlanButton";
import styles from "./PlanCard.module.css";

type BillingCycle = "MONTHLY" | "YEARLY";
interface PlanCardProps {
  plan: PlanVo;
  billingCycle: BillingCycle;
  freeCreditAmount?: number;
  index: number;
  plans: PlanVo[];
  currentPlanCode?: string;
  yearlyDiscount?: string;
  subscriptionBonus?: number;
}

export function PlanCard({
  plan,
  billingCycle,
  freeCreditAmount,
  index,
  plans,
  currentPlanCode,
  yearlyDiscount,
  subscriptionBonus = 0,
}: PlanCardProps) {
  const isCurrentPlan = plan.planCode === currentPlanCode;

  return (
    <div
      className={classNames(
        "relative h-full",
        plan.recommend ? "mt-9 lg:mt-0" : ""
      )}
    >
      {plan.recommend && (
        <div className="absolute -left-[6px] -right-[6px] -bottom-[6px] -top-9 rounded-[20px] bg-primary z-1 pt-1.5 text-center">
          <strong className="text-white text-base font-normal">
            Most Popular
          </strong>
        </div>
      )}

      <div
        className={classNames(
          "relative h-full z-10 rounded-[20px] bg-background-card p-6 flex flex-col backdrop-blur-sm border border-primary/[.07] shadow-[0_3.45px_29.785px_0_rgba(0,85,255,0.01)]",
          isFreePlan(plan) ? "" : styles.subscriptionCard,
          plan.recommend ? styles.recommendCard : ""
        )}
      >
        <h2 className="text-base text-text-primary font-bold flex items-center justify-between">
          <span>{plan.memberShipName}</span>
          {billingCycle === "YEARLY" && !isFreePlan(plan) && (
            <span className="rounded-[6px] px-[10px] py-1 bg-primary text-white text-xs">
              Save {yearlyDiscount?.replace("-", "")}
            </span>
          )}
          {isFreePlan(plan) && (
            <span className="rounded-[6px] px-[10px] py-1 bg-primary text-white text-xs">
              Limited Time Offer
            </span>
          )}
        </h2>
        <p className="flex items-end text-sm text-text-primary">
          <span className="text-[42px] font-medium ">
            $
            {billingCycle === "YEARLY"
              ? (Number(plan.price || 0) / 12).toFixed(0)
              : Number(plan.price || 0)}
          </span>
          <span className="text-base font-normal mb-[10px]">/month</span>
          {billingCycle === "YEARLY" && !isFreePlan(plan) && (
            <span className="ml-2 text-base font-normal mb-[10px] text-text-secondary">
              ${formatNumberWithCommas(Number(plan.price) || 0)}/year
            </span>
          )}
        </p>

        <p className="text-text-tertiary mt-1.5 text-xs font-normal overflow-hidden text-ellipsis line-clamp-2">
          {plan.description}
        </p>

        <PlanButton
          className="mt-6"
          plan={plan}
          isCurrentPlan={isCurrentPlan}
          subscriptionBonus={subscriptionBonus}
          onClick={() => {
            if (isCurrentPlan) {
              return;
            }

            logger.event(VidMuseEvent.PricingPagePlanClick, {
              billing_cycle: billingCycle,
              plan_code: plan.planCode,
            });

            if (isFreePlan(plan)) {
              window.location.href = "/";
              return;
            }

            window.open(
              `/payment/sessions/checkout?priceId=${plan.priceId}`,
              "_blank",
              "noopener,noreferrer"
            );
          }}
        />

        <div
          className="rounded-xl mt-6 p-3"
          style={{
            background: isFreePlan(plan)
              ? "rgba(255, 255, 255, 0.04)"
              : "linear-gradient(0deg, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0.14) 100%), linear-gradient(100deg, #19193B 7.61%, #151524 32.78%, #131217 57.64%, #1F0B1D 83.73%, #3E0D36 108.56%)",
          }}
        >
          <p
            className={classNames(
              "flex items-center gap-1 ",
              isFreePlan(plan) ? "text-text-primary" : "text-primary"
            )}
          >
            <Image src={creditIcon} alt="credit" width={14} height={14} />
            <span className="text-base font-bold">
              {formatNumberWithCommas(
                isFreePlan(plan)
                  ? freeCreditAmount || ""
                  : plan.creditAmount || ""
              )}
            </span>
            {isFreePlan(plan) ? (
              <span>one-time credits</span>
            ) : (
              <span>monthly credits</span>
            )}
          </p>
          <p className="mt-1 flex items-start gap-[10px] pl-[5px]">
            <span className="mt-2 w-1 h-1 rounded-full bg-text-tertiary block"></span>
            <span className="text-xs font-normal leading-normal text-text-tertiary">
              {isFreePlan(plan) ? (
                <span>
                  Join our
                  <Link
                    className="text-text-primary! font-medium underline! hover:text-primary! mx-1"
                    href={DISCORD_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Discord
                  </Link>
                  or
                  <Popover
                    trigger={["hover"]}
                    placement="top"
                    arrow={false}
                    classNames={{
                      root: "!pt-2",
                      body: classNames(
                        "!p-6 !pb-[22px] flex bg-white! !rounded-xl !w-[198px]",
                        styles.popoverBody
                      ),
                    }}
                    content={
                      <>
                        <div className="text-black text-sm font-normal rounded-none flex flex-col items-center">
                          <Image
                            src="/wechat-qrcode.png"
                            alt="Wechat QR Code"
                            width={150}
                            height={150}
                            className="w-full h-auto"
                          />
                          <p className="mt-3 text-center">
                            Welcome to join our WeChat community
                          </p>
                        </div>
                      </>
                    }
                  >
                    <span className="text-text-primary! font-medium underline! hover:text-primary! mx-1">
                      WeChat
                    </span>
                  </Popover>
                  for exclusive credits giveaways
                </span>
              ) : (
                <span>{plan.tooltipForCredit}</span>
              )}
            </span>
          </p>
        </div>

        <div className="mt-5 text-text-tertiary text-xs font-normal">
          <p>
            {index === 0
              ? "Plan includes:"
              : `All features in ${
                  plans[index - 1]?.memberShipName || "Free"
                }, plus:`}
          </p>

          <ul className="mt-3 flex flex-col gap-3">
            {(plan.featureList || []).map((feature, featureIndex) => (
              <li
                key={`${plan.planCode}-feature-${featureIndex}`}
                className="flex items-start gap-[6px]"
              >
                <CheckedIcon className="shrink-0 w-3 h-3 mt-0.5 text-primary" />
                {feature.description}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
